/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { groupBy, isNil, keyBy, sum } from 'lodash-es'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { getPlanningAreaOptions, getPlanningDataOptions, getRoutesInAreaOptions } from '@/api/main'
import { getRoute } from '@/api/main/sdk.gen'
import type { RouteSegment, StreetNodePlanningData, StreetSegmentPlanningData } from '@/api/main/types.gen'
import { resolveRouteChangeReset } from './createModePolicy'
import { shouldClearPlanOnInitialRouteSync } from './geojsonBuilders'
import type { GapFix } from './gapFix'
import {
  initialPlannerState,
  plannerReducer,
  selectCanAcceptTail,
  selectCanCommitEdit,
  selectCanUndoOperation,
  selectCanUndoSegment,
  selectEffectivePath,
  selectHasUnsavedChanges,
  selectPathNodes,
  selectRouteFitKey,
} from './plannerCore'
import { DEFAULT_PLANNER_CONTROL_SCHEMA, planningDataSchemaHeaders, type PlannerControlSchema } from './schemaSwitch'
import { computeSegmentsConnectedToUnblockedNetwork } from './segmentFilters'
import type { ActivePicker, InteractionMode, PendingRemoval, RouteId } from './types'

function routeKey(route: RouteId | undefined): string | null {
  return route?.id && route.version ? `${route.id}\0${route.version}` : null
}

export function useRoutePlannerLogic(
  areaId: string,
  initialRoute?: RouteId,
  initialMode: InteractionMode = 'edit',
  controlDataSchema: PlannerControlSchema = DEFAULT_PLANNER_CONTROL_SCHEMA
) {
  const [core, dispatch] = useReducer(plannerReducer, initialMode, initialPlannerState)
  const [selectedRoute, setSelectedRouteId] = useState<RouteId | undefined>(initialRoute)

  const { data: planningArea } = useSuspenseQuery(
    getPlanningAreaOptions({
      path: { area_id: areaId },
    })
  )

  const { data: planningData } = useSuspenseQuery(
    getPlanningDataOptions({
      path: { area_id: areaId },
      query: { include_within: 0 },
      headers: planningDataSchemaHeaders(controlDataSchema),
    })
  )

  const { data: existingRoutes } = useSuspenseQuery(
    getRoutesInAreaOptions({
      path: { area_id: areaId },
    })
  )

  const streetSegments = planningData.street_segments
  const streetNodes = planningData.street_nodes
  const signPosts = planningData.sign_posts
  const technicalSegments = planningData.technical_segments

  const sccSegmentIds = useMemo(() => computeSegmentsConnectedToUnblockedNetwork(streetSegments), [streetSegments])

  const nonBlockedStreetSegments = useMemo(
    () => streetSegments.filter(it => !it.is_blocked && sccSegmentIds.has(it.street_segment_id)),
    [streetSegments, sccSegmentIds]
  )

  const indexedSegments = useMemo(
    () => keyBy(nonBlockedStreetSegments, (it: StreetSegmentPlanningData) => it.street_segment_id),
    [nonBlockedStreetSegments]
  )

  const indexedAllSegments = useMemo(
    () => keyBy(streetSegments, (it: StreetSegmentPlanningData) => it.street_segment_id),
    [streetSegments]
  )

  const clearPlanState = useCallback(() => {
    dispatch({ type: 'CLEAR_PLAN' })
  }, [])

  const invalidateLoadedRoute = useCallback(() => {
    dispatch({ type: 'INVALIDATE_LOADED_ROUTE' })
  }, [])

  const prevInitialRouteKeyRef = useRef<string | null | undefined>(undefined)
  const loadedRouteKeyRef = useRef(core.loadedRouteKey)
  loadedRouteKeyRef.current = core.loadedRouteKey
  useEffect(() => {
    const nextKey = routeKey(initialRoute)
    const prevKey = prevInitialRouteKeyRef.current
    if (prevKey === nextKey) return
    prevInitialRouteKeyRef.current = nextKey
    setSelectedRouteId(initialRoute)
    if (
      !shouldClearPlanOnInitialRouteSync({
        nextKey,
        loadedRouteKey: loadedRouteKeyRef.current,
      })
    ) {
      return
    }
    if (nextKey) {
      invalidateLoadedRoute()
    } else {
      clearPlanState()
    }
  }, [initialRoute?.id, initialRoute?.version])

  const listedAsExisting = useMemo(
    () =>
      Boolean(
        selectedRoute && existingRoutes.some(r => r.id === selectedRoute.id && r.version === selectedRoute.version)
      ),
    [existingRoutes, selectedRoute]
  )

  const routePlanQuery = useQuery({
    queryKey: ['routePlanner', 'getRoute', selectedRoute?.id, selectedRoute?.version],
    enabled: Boolean(selectedRoute?.id && selectedRoute?.version),
    retry: false,
    queryFn: async () => {
      if (!selectedRoute) return null
      const { data } = await getRoute({
        path: { route_id: selectedRoute.id, route_version: selectedRoute.version },
        throwOnError: true,
      })
      return data
    },
  })

  const routeExists = Boolean(routePlanQuery.data) || listedAsExisting
  const routeLoadError = listedAsExisting && routePlanQuery.isError

  useEffect(() => {
    const key = routeKey(selectedRoute)
    if (!key) return
    const data = routePlanQuery.data?.route_plan
    if (isNil(data)) return
    if (core.loadedRouteKey === key) return
    dispatch({ type: 'LOAD_PLAN', segments: data, routeKey: key })
  }, [routePlanQuery.data?.route_plan, selectedRoute?.id, selectedRoute?.version, core.loadedRouteKey])

  const isListedRoute = useCallback(
    (route: RouteId) => existingRoutes.some(r => r.id === route.id && r.version === route.version),
    [existingRoutes]
  )

  const selectedRouteRef = useRef(selectedRoute)
  selectedRouteRef.current = selectedRoute

  const setSelectedRoute = useCallback(
    (next: RouteId | undefined) => {
      switch (resolveRouteChangeReset(selectedRouteRef.current, next, isListedRoute)) {
        case 'refit':
          dispatch({ type: 'REQUEST_ROUTE_REFIT' })
          break
        case 'invalidate':
          invalidateLoadedRoute()
          break
        case 'clear':
          clearPlanState()
          break
        case 'none':
          break
      }
      selectedRouteRef.current = next
      setSelectedRouteId(next)
    },
    [isListedRoute, clearPlanState, invalidateLoadedRoute]
  )

  // Path and edit-session actions — all dispatch into the planner core, which owns
  // history snapshots, transient clearing, and edit invariants.

  const appendToPath = useCallback(
    (exitNode: Pick<StreetNodePlanningData, 'street_node_id'>, segment?: StreetSegmentPlanningData) =>
      dispatch({ type: 'APPEND', nodeId: exitNode.street_node_id, segment }),
    []
  )

  const deleteLastSegment = useCallback(() => dispatch({ type: 'DELETE_LAST' }), [])
  const startEdit = useCallback((segmentIndex: number) => dispatch({ type: 'START_EDIT', segmentIndex }), [])
  const cancelEdit = useCallback(() => dispatch({ type: 'CANCEL_EDIT' }), [])
  const commitEdit = useCallback(() => dispatch({ type: 'COMMIT_EDIT' }), [])
  const acceptTail = useCallback(() => dispatch({ type: 'ACCEPT_TAIL' }), [])
  const acceptShortestPath = useCallback((hint: RouteSegment[]) => dispatch({ type: 'ACCEPT_HINT', hint }), [])
  const removeLoop = useCallback(
    (loopStart: number, loopEnd: number, replacement: RouteSegment[] = []) =>
      dispatch({ type: 'CONFIRM_REMOVAL', loopStart, loopEnd, replacement }),
    []
  )
  const applyGapFixes = useCallback((fixes: GapFix[]) => dispatch({ type: 'APPLY_GAP_FIXES', fixes }), [])
  const undoOperation = useCallback(() => dispatch({ type: 'UNDO_OPERATION' }), [])
  const resetPathHistory = useCallback(() => dispatch({ type: 'RESET_HISTORY' }), [])
  const resetToOriginal = useCallback(() => dispatch({ type: 'RESET_TO_ORIGINAL' }), [])

  // Transient interaction state and mode also live in the core so mutations clear
  // them as an invariant rather than by call-site discipline.
  const setInteractionMode = useCallback((mode: InteractionMode) => dispatch({ type: 'SET_MODE', mode }), [])
  const setAddTarget = useCallback((nodeId: string | null) => dispatch({ type: 'SET_ADD_TARGET', nodeId }), [])
  const setPendingRemoval = useCallback(
    (pendingRemoval: PendingRemoval | null) => dispatch({ type: 'SET_PENDING_REMOVAL', pendingRemoval }),
    []
  )
  const setActivePicker = useCallback(
    (picker: ActivePicker | null) => dispatch({ type: 'SET_ACTIVE_PICKER', picker }),
    []
  )
  const setGapFixPreview = useCallback((preview: boolean) => dispatch({ type: 'SET_GAP_FIX_PREVIEW', preview }), [])
  const applyRemovalAtIndex = useCallback(
    (visitIndex: number) => dispatch({ type: 'MERGE_REMOVAL_VISIT', visitIndex }),
    []
  )
  const clearTransients = useCallback(() => dispatch({ type: 'CLEAR_TRANSIENTS' }), [])

  const adoptSavedPlan = useCallback(
    (segments: RouteSegment[], route: RouteId) =>
      dispatch({ type: 'ADOPT_SAVED', segments, routeKey: routeKey(route) }),
    []
  )

  const pathNodes = useMemo(() => selectPathNodes(core), [core.path])
  const effectivePathState = useMemo(() => selectEffectivePath(core), [core.path, core.edit])
  const hasUnsavedChanges = useMemo(() => selectHasUnsavedChanges(core), [core.path, core.original])
  const canUndoSegment = useMemo(() => selectCanUndoSegment(core), [core.path, core.edit, core.original])

  const continuationSegments = useMemo(
    () =>
      nonBlockedStreetSegments.filter(
        it =>
          it.source_node_id === core.path.lastNode ||
          (it.destination_node_id === core.path.lastNode && it.is_bidirectional)
      ),
    [nonBlockedStreetSegments, core.path.lastNode]
  )

  const drivesBySegment = useMemo(() => {
    const grouped = groupBy(
      effectivePathState.segments
        .filter(({ street_segment_id }) => indexedSegments[street_segment_id] !== undefined)
        .map(pathSegment => {
          const segment = indexedSegments[pathSegment.street_segment_id]!
          return {
            segmentId: pathSegment.street_segment_id,
            normal: Number(
              !segment.is_bidirectional ||
                (pathSegment.entrance_node_id === segment.source_node_id &&
                  pathSegment.exit_node_id === segment.destination_node_id)
            ),
            reverse: Number(
              segment.is_bidirectional &&
                pathSegment.entrance_node_id === segment.destination_node_id &&
                pathSegment.exit_node_id === segment.source_node_id
            ),
          }
        }),
      ({ segmentId }) => segmentId
    )

    return Object.fromEntries(
      Object.entries(grouped).map(([segmentId, items]) => [
        segmentId,
        {
          normal: sum((items ?? []).map(it => it.normal)),
          reverse: sum((items ?? []).map(it => it.reverse)),
        },
      ])
    )
  }, [effectivePathState.segments, indexedSegments])

  const isSegmentCovered = useCallback(
    (segmentId: string) => {
      const segment = indexedSegments[segmentId]
      if (isNil(segment)) return false
      const normalDrives = drivesBySegment[segmentId]?.normal ?? 0
      const reverseDrives = drivesBySegment[segmentId]?.reverse ?? 0
      return (
        normalDrives >= segment.requested_drives &&
        (!segment.is_bidirectional || reverseDrives >= segment.requested_drives)
      )
    },
    [indexedSegments, drivesBySegment]
  )

  return {
    core,
    dispatch,
    pathState: core.path,
    editState: core.edit,
    originalLoadedSegments: core.original,
    interactionMode: core.mode,
    setInteractionMode,
    addTarget: core.addTarget,
    setAddTarget,
    pendingRemoval: core.pendingRemoval,
    setPendingRemoval,
    activePicker: core.activePicker,
    setActivePicker,
    gapFixPreview: core.gapFixPreview,
    setGapFixPreview,
    applyRemovalAtIndex,
    clearTransients,
    pathNodes,
    effectivePathState,
    hasUnsavedChanges,
    canUndoSegment,
    continuationSegments,
    appendToPath,
    deleteLastSegment,
    startEdit,
    cancelEdit,
    commitEdit,
    canCommitEdit: selectCanCommitEdit(core),
    acceptTail,
    canAcceptTail: selectCanAcceptTail(core),
    acceptShortestPath,
    removeLoop,
    applyGapFixes,
    undoOperation,
    canUndoOperation: selectCanUndoOperation(core),
    resetPathHistory,
    resetToOriginal,
    adoptSavedPlan,
    clearPlanState,
    planningArea,
    polygon: planningArea.bounds.points,
    existingRoutes,
    selectedRoute,
    setSelectedRoute,
    loadedRouteKey: core.loadedRouteKey,
    routeFitKey: selectRouteFitKey(core),
    indexedSegments,
    indexedAllSegments,
    streetNodes,
    signPosts,
    technicalSegments,
    drivesBySegment,
    isSegmentCovered,
    isRoutePlanLoading: routePlanQuery.isFetching,
    nonBlockedStreetSegments,
    streetSegments,
    routeExists,
    routeLoadError,
  }
}
